import React, { useEffect, useRef } from "react";
import GalaxyBackground from "./GalaxyBackground";
import NeuralinkBrain3D from "./NeuralinkBrain3D";

/**
 * ThemedBackground — single component that renders WHATEVER theme.background
 * is currently set. Previously only `galaxy` had a renderer, so picking
 * neural_brain / waveform / data_grid / midnight_blue etc resulted in a
 * black screen (the value persisted but nothing painted).
 *
 * `lite` prop — when true (mirror window), force a static gradient
 * fallback instead of any heavy animated/3D renderer. Owner saw "Pages
 * Unresponsive" because running two heavy backgrounds (orb window +
 * mirror window) at once was killing the GPU. The mirror still gets the
 * cinematic look via gradients, but no canvas/three.js loops.
 *
 * Renderers:
 *   galaxy          — existing canvas starfield
 *   neural_brain    — animated circuit / synapse nodes
 *   waveform        — cyan voice-waveform pulse
 *   data_grid       — cyan rotating cube grid
 *   tinted_black    — subtle gold-tinted gradient
 *   pure_black      — flat black
 *   midnight_blue   — deep navy gradient
 *   dark_purple     — purple gradient
 *   deep_green      — green gradient
 *   <anything else> — falls back to galaxy so the orb never sits on a
 *                      flat-black void (user complaint: "no backgrounds work")
 */
export default function ThemedBackground({ background, lite = false }) {
  // Owner complaint: "first 3 seconds shows a different background, then
  // flicks to the one I've picked". Cause: theme hadn't loaded yet so we
  // were rendering galaxy as a placeholder. Skip rendering until we have
  // the real value — caller passes `undefined` while loading, real string
  // once the API call returns. Pure black stays during that window so the
  // screen looks intentional rather than flickering.
  if (!background) return <div style={baseStyle("#000")} />;
  const bg = background.toLowerCase();

  // 🚨 LITE MODE — mirror window swaps every animated/3D background for a
  // matching static gradient so it doesn't compete with the orb window's
  // heavy renderer. Same colour palette, zero animation cost.
  if (lite) {
    if (bg === "galaxy")           return <div style={{ ...baseStyle("#000"), background: "radial-gradient(ellipse at center, #08081a 0%, #000 75%)" }} />;
    if (bg === "neural_brain" || bg === "neuralink_brain" || bg === "neuralink_brain_3d")
                                   return <div style={{ ...baseStyle("#020410"), background: "radial-gradient(ellipse at center, #1a1208 0%, #020410 70%)" }} />;
    if (bg === "waveform")         return <div style={{ ...baseStyle("#020810"), background: "radial-gradient(ellipse at center, #082030 0%, #02060c 70%)" }} />;
    if (bg === "data_grid")        return <div style={{ ...baseStyle("#020a14"), background: "linear-gradient(135deg, #020a14 0%, #061828 100%)" }} />;
    // Solid gradients fall through to the normal renderer (they're already cheap)
  }

  if (bg === "galaxy") return <GalaxyBackground active={true} />;
  if (bg === "pure_black") return <div style={baseStyle("#000")} />;
  if (bg === "tinted_black") return (
    <div style={{ ...baseStyle("#000"), background: "radial-gradient(ellipse at center, #1a1208 0%, #000 70%)" }} />
  );
  if (bg === "midnight_blue") return (
    <div style={{ ...baseStyle("#040814"), background: "radial-gradient(ellipse at center, #0a1530 0%, #02040a 70%)" }} />
  );
  if (bg === "dark_purple") return (
    <div style={{ ...baseStyle("#0a0418"), background: "radial-gradient(ellipse at center, #1d0a3a 0%, #06010f 70%)" }} />
  );
  if (bg === "deep_green") return (
    <div style={{ ...baseStyle("#020a06"), background: "radial-gradient(ellipse at center, #042918 0%, #010604 70%)" }} />
  );
  if (bg === "neural_brain") return <NeuralBrain />;
  if (bg === "neuralink_brain") return <NeuralinkBrain3D />;
  if (bg === "neuralink_brain_3d") return <NeuralinkBrain3D />;
  if (bg === "waveform") return <Waveform />;
  if (bg === "data_grid") return <DataGrid />;

  // Unknown / new value → galaxy fallback so screen is never blank black.
  return <GalaxyBackground active={true} />;
}

const baseStyle = (bg) => ({
  position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
  background: bg,
});

/* ─────────── NEURAL BRAIN ─────────── */
/** Animated network of nodes with synapse lines pulsing between them. */
function NeuralBrain() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = canvas.width = window.innerWidth;
    let h = canvas.height = window.innerHeight;
    const onResize = () => { w = canvas.width = window.innerWidth; h = canvas.height = window.innerHeight; };
    window.addEventListener("resize", onResize);

    // Pause when tab hidden — 60fps O(N²) loop was burning CPU on
    // background tabs and starving the foreground window. Owner saw
    // "Pages Unresponsive" with both tabs hung at once.
    let paused = document.hidden;
    const onVis = () => { paused = document.hidden; };
    document.addEventListener("visibilitychange", onVis);

    // Halved node count (60→30) — edge loop is O(N²), so this is a 4x
    // perf win with no visible loss on the background layer.
    const N = 30;
    const nodes = Array.from({ length: N }, () => ({
      x: Math.random() * w, y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.15, vy: (Math.random() - 0.5) * 0.15,
      r: 1.5 + Math.random() * 1.5,
    }));

    let raf;
    let t0 = performance.now();
    const tick = () => {
      if (paused) { raf = requestAnimationFrame(tick); return; }
      const t = (performance.now() - t0) / 1000;
      ctx.fillStyle = "rgba(2,4,12,0.18)";
      ctx.fillRect(0, 0, w, h);

      // Draw edges between near nodes
      for (let i = 0; i < N; i++) {
        const a = nodes[i];
        a.x += a.vx; a.y += a.vy;
        if (a.x < 0 || a.x > w) a.vx *= -1;
        if (a.y < 0 || a.y > h) a.vy *= -1;
        for (let j = i + 1; j < N; j++) {
          const b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < 160) {
            const pulse = 0.5 + 0.5 * Math.sin(t * 1.5 + (i + j) * 0.4);
            ctx.strokeStyle = `rgba(255,184,0,${(0.18 - d / 1100) * (0.5 + pulse * 0.5)})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      // Draw nodes
      for (const n of nodes) {
        const glow = 0.6 + 0.4 * Math.sin(t * 2 + n.x * 0.01);
        ctx.fillStyle = `rgba(255,200,80,${0.5 * glow})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
  return <canvas ref={canvasRef} data-testid="bg-neural-brain"
    style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", background: "#020410" }} />;
}

/* ─────────── WAVEFORM ─────────── */
/** Cyan voice-style waveform pulsing across the screen, layered. */
function Waveform() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = canvas.width = window.innerWidth;
    let h = canvas.height = window.innerHeight;
    const onResize = () => { w = canvas.width = window.innerWidth; h = canvas.height = window.innerHeight; };
    window.addEventListener("resize", onResize);

    let raf;
    let paused = false;
    let t0 = performance.now();
    const onVis = () => { paused = document.hidden; if (!paused) t0 = performance.now(); };
    document.addEventListener("visibilitychange", onVis);
    const tick = () => {
      if (paused) { raf = requestAnimationFrame(tick); return; }
      const t = (performance.now() - t0) / 1000;
      ctx.fillStyle = "rgba(2,8,16,0.22)";
      ctx.fillRect(0, 0, w, h);

      // Three layered sine waves
      const palette = ["#7CFFE9", "#5BC8FF", "#a78bfa"];
      for (let layer = 0; layer < 3; layer++) {
        ctx.strokeStyle = palette[layer] + "44";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        const amp = 60 + layer * 28;
        const freq = 0.006 + layer * 0.002;
        const phase = t * (1 + layer * 0.4);
        for (let x = 0; x <= w; x += 4) {
          const y = h / 2 + Math.sin(x * freq + phase) * amp * (0.5 + 0.5 * Math.sin(t * 0.7 + x * 0.002));
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
  return <canvas ref={canvasRef} data-testid="bg-waveform"
    style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", background: "#020810" }} />;
}

/* ─────────── DATA GRID ─────────── */
/** Cyan rotating cube/dot grid — gives a "data flow" feel. */
function DataGrid() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = canvas.width = window.innerWidth;
    let h = canvas.height = window.innerHeight;
    const onResize = () => { w = canvas.width = window.innerWidth; h = canvas.height = window.innerHeight; };
    window.addEventListener("resize", onResize);

    let raf;
    let paused = false;
    let t0 = performance.now();
    const onVis = () => { paused = document.hidden; if (!paused) t0 = performance.now(); };
    document.addEventListener("visibilitychange", onVis);
    const STEP = 60;
    const tick = () => {
      if (paused) { raf = requestAnimationFrame(tick); return; }
      const t = (performance.now() - t0) / 1000;
      ctx.fillStyle = "#020a14";
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = "rgba(120,200,255,0.55)";
      ctx.strokeStyle = "rgba(80,160,220,0.18)";
      ctx.lineWidth = 1;

      // Vertical lines, drifting upward — illusion of data flow
      const offset = (t * 18) % STEP;
      for (let x = STEP; x < w; x += STEP) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
      }
      for (let y = -STEP + offset; y < h; y += STEP) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
      }

      // Pulsing dots at intersections
      for (let x = STEP; x < w; x += STEP) {
        for (let y = -STEP + offset; y < h; y += STEP) {
          const pulse = 0.4 + 0.6 * Math.sin(t * 2 + x * 0.01 + y * 0.01);
          if (pulse > 0.8) {
            ctx.fillRect(x - 1, y - 1, 2, 2);
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
  return <canvas ref={canvasRef} data-testid="bg-data-grid"
    style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", background: "#020a14" }} />;
}
